import React, { useRef, useEffect, useContext, useState, useMemo } from "react";
import SearchIcon from "assets/svgs/dropdown-search-icon.svg";
import { ReactComponent as SelectArrow } from "assets/svgs/select-arrow.svg";
import useSearch from "hooks/useSearch";
import useToggle from "hooks/useToggle";
import Input from "components/input";
import { ProductFinderContext } from "@root/context/product-finder";

const Search = ({ search, handleSearchChange, optionProperty }) => (
    <div className="relative w-full px-4 mb-2">
        <Input
            placeholder={`Type your ${optionProperty}`}
            className="input"
            type="text"
            value={search}
            setValue={handleSearchChange}
            autoFocus
        />
        <img src={SearchIcon} className="absolute right-6 top-2.5 w-4 h-4 bg-cover" />
    </div>
);

const DisabledDropdown = ({ selected, className, dropdownNumber }) => (
    <div className={`flex flex-col gap-0 opacity-70 w-60 h-8 ${className} ymm-dropdown-text-disabled`}>
        <div className="flex flex-col md:py-2.5 py-1.5 px-2 bg-white rounded-lg relative">
            <div className="flex items-center">
                {/* <div className="dropdown-number ymm-dropdown flex items-center justify-center w-6 h-6 rounded-full bg-[#4E4D4D] text-white mr-2"> */}
                {/*    {dropdownNumber} */}
                {/* </div> */}
                <button type="button" className="ymm-dropdown-text text-gray-701 text-left md:text-base text-base-xs">
                    {selected}
                </button>
            </div>
            <SelectArrow className="shopping-feed-finder__dropdown-arrow absolute w-2.5 h-1.5 right-4 md:top-[18px] top-4" />
        </div>
    </div>
);

export const Dropdown = ({ selected, options, optionProperty, onSelect, hasSearch, className, disabled, dropdownNumber, hasValue, allowCustom, openWhenEnabled, topOptions = [] }) => {
    const { isOpen, toggle, open } = useToggle();
    const { search, resetSearch, filteredOptions, handleSearchChange } = useSearch(options, optionProperty, allowCustom);
    const divRef = useRef(null);
    const dropdownRef = useRef(null);
    const itemRefs = useRef([]);
    const { errorField, setErrorField, errorFieldRefresher } = useContext(ProductFinderContext);
    const [focusedIndex, setFocusedIndex] = useState(0);
    const [optionsMaxHeight, setOptionsMaxHeight] = useState(450);

    // When topOptions has items, split the (search-filtered) options into a
    // "Top Items" group and an "Others" group. `displayOptions` is the flat,
    // ordered list that drives keyboard navigation, scrolling and highlighting.
    const hasTopOptions = Array.isArray(topOptions) && topOptions?.length > 0;
    const { topGroup, otherGroup, displayOptions, hasGroups } = useMemo(() => {
        if (!hasTopOptions) {
            return { topGroup: [], otherGroup: filteredOptions, displayOptions: filteredOptions, hasGroups: false };
        }
        const topValues = new Set(topOptions.map((option) => option[optionProperty]));
        let top = filteredOptions.filter((option) => topValues.has(option[optionProperty]));
        top = top.sort((a, b) => a.count - b.count);
        // Keep the top items inside the full "All" list as well, so they are not
        // removed from the complete set of options.
        const others = filteredOptions;
        return {
            topGroup: top,
            otherGroup: others,
            displayOptions: [...top, ...others],
            hasGroups: top.length > 0,
        };
    }, [filteredOptions, topOptions, optionProperty, hasTopOptions]);

    const selectedScrollIndex = useMemo(() => {
        if (hasGroups) {
            const topIndex = topGroup.findIndex((option) => option[optionProperty] === selected);
            if (topIndex !== -1) return topIndex;
            const otherIndex = otherGroup.findIndex((option) => option[optionProperty] === selected);
            return otherIndex !== -1 ? topGroup.length + otherIndex : -1;
        }
        return displayOptions.findIndex((option) => option[optionProperty] === selected);
    }, [hasGroups, topGroup, otherGroup, displayOptions, selected, optionProperty]);

    useEffect(() => {
        if (errorField && errorField === optionProperty) {
            toggle();
        }
    }, [errorField, errorFieldRefresher]);

    useEffect(() => {
        if (!isOpen || selectedScrollIndex < 0 || window.innerWidth < 1024) return undefined;

        const scrollToSelected = () => {
            const selectedEl = itemRefs.current[selectedScrollIndex];
            if (!selectedEl || !divRef.current) return;
            divRef.current.scrollTop = selectedEl.offsetTop - 140;
        };

        scrollToSelected();
        requestAnimationFrame(scrollToSelected);
        return undefined;
    }, [isOpen, selectedScrollIndex]);

   useEffect(() => {
        if (!isOpen) return undefined;

        const BOTTOM_GAP = 64;
        const MAX_HEIGHT = 450;
        const TIGHT_SPACE_THRESHOLD = 300;

        const updateMaxHeight = () => {
            if (!divRef.current) return;
            const { top } = divRef.current.getBoundingClientRect();
            const rawAvailable = window.innerHeight - top;
            const availableWithGap = rawAvailable - BOTTOM_GAP;
            const available = availableWithGap < TIGHT_SPACE_THRESHOLD ? rawAvailable : availableWithGap;
            setOptionsMaxHeight(Math.max(0, Math.min(MAX_HEIGHT, available)));
        };

        updateMaxHeight();
        window.addEventListener("resize", updateMaxHeight);
        window.addEventListener("scroll", updateMaxHeight, true);
        return () => {
            window.removeEventListener("resize", updateMaxHeight);
            window.removeEventListener("scroll", updateMaxHeight, true);
        };
    }, [isOpen, displayOptions]);

    // if openWhenEnabled is true, open the dropdown when the component mounts
    useEffect(() => {
        if (openWhenEnabled && !disabled) {
            open();
        }
    }, [disabled, options]);

    // when the option list changes, reset focusedIndex to the selected index or 0
    useEffect(() => {
        const nextIndex = selectedScrollIndex !== -1 ? selectedScrollIndex : 0;
        setFocusedIndex(nextIndex);
        itemRefs.current?.[nextIndex]?.scrollIntoView({ behavior: "instant", block: "nearest" });
    }, [displayOptions, selectedScrollIndex]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target) && isOpen) {
                toggle();
                resetSearch();
            }
        };

        const handleKeyDown = (event) => {
            if (!isOpen) return;

            switch (event.key) {
                case "ArrowDown":
                    event.preventDefault();
                    setFocusedIndex((prevIndex) => {
                        const index = prevIndex < displayOptions.length - 1 ? prevIndex + 1 : prevIndex;
                        itemRefs.current?.[index]?.scrollIntoView({ behavior: "smooth", block: "nearest" });
                        return index;
                    });
                    break;
                case "ArrowUp":
                    event.preventDefault();
                    setFocusedIndex((prevIndex) => {
                        const index = (prevIndex > 0 ? prevIndex - 1 : 0);
                        itemRefs.current?.[index]?.scrollIntoView({ behavior: "smooth", block: "nearest" });
                        return index;
                    });
                    break;
                case "Enter":
                    event.preventDefault();
                    if (focusedIndex !== -1 && displayOptions[focusedIndex]?.[optionProperty]) {
                        handleSelect(optionProperty, displayOptions[focusedIndex]?.[optionProperty]);
                    }
                    break;
                case "Escape":
                    toggle();
                    resetSearch();
                    break;
                default:
                    break;
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        document.addEventListener("keydown", handleKeyDown);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [isOpen, focusedIndex, displayOptions]);

    // on open reset index to selected one
    useEffect(() => {
        if (!isOpen) {
            setFocusedIndex(selectedScrollIndex !== -1 ? selectedScrollIndex : 0);
        }
    }, [isOpen, selectedScrollIndex]);

    if (disabled) return <DisabledDropdown selected={selected} className={className} dropdownNumber={dropdownNumber} />;

    const handleSelect = (dropdownName, selectedValue) => {
        if (selectedValue === `Create \`${search}\``) {
            // eslint-disable-next-line no-param-reassign
            selectedValue = search;
        }
        onSelect(dropdownName, selectedValue);
        toggle();
        resetSearch();
        setErrorField(null);
    };

    // `index` is the position inside the flat `displayOptions` list so that
    // keyboard focus, hover and selection stay in sync across both groups.
    const renderOption = (option, index) => {
        const isSelected = selected === option[optionProperty];
        const isFocused = focusedIndex === index;
        return (
            <div
                key={`${option[optionProperty]}-${index}`}
                onMouseOver={() => setFocusedIndex(index)}
                className={`flex items-start transition-all duration-300 hover:bg-yellow-600 ${isSelected && "bg-yellow-600"} ${isFocused ? "bg-yellow-600" : ""}`}
                ref={(el) => {
                    itemRefs.current[index] = el;
                }}
            >
                <button
                    type="button"
                    className="ymm-dropdown-item__options w-full text-left focus-visible:bg-yellow-600 focus-visible:outline-0 pl-4 pr-2 py-1"
                    onClick={() => handleSelect(optionProperty, option[optionProperty])}
                >
                    {option[optionProperty]}
                </button>
            </div>
        );
    };

    return (
        <div ref={dropdownRef} className={`flex flex-col gap-0 w-60 flex-1 h-8 min-w-0 basis-full ${className} ${topOptions?.length > 0 ? "dropdown-desktop__with-top-options" : ""}`}>
            <div className={`ymm-dropdown-active ${hasValue && "ymm-dropdown-active--has-value"}`}>
                <button
                    type="button"
                    className="text-gray-701 text-left px-2 pr-4 md:py-2.5 py-1.5 md:text-base text-base-xs flex items-center justify-between w-full relative overflow-hidden"
                    onClick={toggle}
                >
                    <div className="flex items-center min-w-0 flex-1 pr-2">
                        {/* <div className="dropdown-number-active flex-shrink-0 flex items-center justify-center w-6 h-6 rounded-full bg-[#FFCB03] text-white mr-2"> */}
                        {/*    {dropdownNumber} */}
                        {/* </div> */}
                        <span className="ymm-dropdown-text-active block overflow-hidden text-ellipsis whitespace-nowrap">
                            {selected}
                        </span>
                    </div>
                    <SelectArrow
                        className={`shopping-feed-finder__dropdown-arrow-active flex-shrink-0 w-2.5 h-1.5 ${isOpen && "rotate-180"}`}
                    />
                </button>
                {(isOpen && hasSearch)
                    && (
                        <Search
                            search={search}
                            optionProperty={optionProperty}
                            handleSearchChange={handleSearchChange}
                            className="mb-1"
                        />
                    )}
                {isOpen && (
                    <div className="md:pb-2.5 pb-1.5">
                        <div
                            ref={divRef}
                            className="dropdown-desktop__options overflow-y-scroll overflow-x-clip bg-white flex flex-col text-left md:text-base text-base-xs gap-0 z-10 pl-0 pr-0"
                            style={{ maxHeight: `${optionsMaxHeight}px` }}
                        >
                            {hasGroups ? (
                                <>
                                    {topGroup.length > 0 && (
                                        <>
                                            <div className="dropdown-desktop__group-label">
                                                {/* pluralize the option property, and in css we capitalize the first letter */}
                                                Popular {optionProperty}s
                                            </div>
                                            {topGroup.map((option, i) => renderOption(option, i))}
                                        </>
                                    )}
                                    {otherGroup.length > 0 && (
                                        <>
                                            <div className="dropdown-desktop__group-label">
                                                {/* pluralize the option property, and in css we capitalize the first letter */}
                                                All {optionProperty}s
                                            </div>
                                            {otherGroup.map((option, i) => renderOption(option, topGroup.length + i))}
                                        </>
                                    )}
                                </>
                            ) : (
                                filteredOptions.map((option, index) => renderOption(option, index))
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Dropdown;
